import { useCallback, useRef, useState } from "react";

/**
 * The running iteration of the exploration a measurement page is on, as
 * ``api/exploration/iteration/number`` reports it: the internal number the
 * backend keys its data by, the user-facing number (P_0 and Q_0 both belong
 * to iteration 0; only Q_n bumps it to n) and the phase.
 *
 * The page renders from ``iteration``; its request handlers read
 * ``iterationRef.current``, which ``applyIterationResponse`` updates at once,
 * so a request fired right after the fetch already carries the number.
 * Nothing is remembered between page loads: every page fetches the
 * iteration of the exploration named in its URL.
 */
export const EMPTY_ITERATION = { number: null, display: null, phase: null };

export function iterationFromResponse(data) {
    const number = data?.iterationNumber ?? null;
    return {
        number,
        display: data?.displayIterationNumber ?? number,
        phase: data?.iterationPhase || null,
    };
}

export default function useIterationState() {
    const [iteration, setIteration] = useState(EMPTY_ITERATION);
    const iterationRef = useRef(EMPTY_ITERATION);
    const applyIterationResponse = useCallback((response) => {
        const next = iterationFromResponse(response?.data);
        iterationRef.current = next;
        setIteration(next);
        return next;
    }, []);
    return { iteration, iterationRef, applyIterationResponse };
}
